import {
  type SetStateAction,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { VALUE_CHAIN_AI_AGENT } from 'constants/general';
import { NOT_FOUND } from 'constants/statusCodes';
import { useGetLocationsCountriesQuery } from 'store/slices/geoLocations/apis/geoLocationsApi';
import valueChainMapApi from 'store/slices/valueChainMap/apis/valueChainMapApi';
import {
  emptyToNull,
  normalizeSupplierTableItem,
} from 'utils/AICreatorHelper';

import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type ISupplierDiscoveryTableItem
  from 'components/AICreatorSupplierUnits/interfaces/ISupplierDiscoveryTableItem';
import type IOption from 'components/MappingModal/interfaces/IOption';
import type IUpdateAgentSessionSupplierDiscoveryItemBody
  from 'store/slices/valueChainMap/interfaces/IUpdateAgentSessionSupplierDiscoveryItemBody';
import type IUseAgentSessionSupplierDiscoveryMutationsParams
  from './interfaces/IUseAgentSessionSupplierDiscoveryMutationsParams';
import type { EVCAgentUnitStatus, IVCAgentSessionUnitUpdatedPayload } from './interfaces/IUseWebSocket';

const useAgentSessionSupplierDiscoveryMutations = ({
  valueChainMapId,
  session,
  currentUnitId,
  tableContextKey,
  tableItems,
  setTableItemsForContext,
  setIsSelectAllChecked,
  setPendingAiUnitIds,
  setSupplierAiSocketPayload,
  syncCurrentSupplierStatusInProgress,
  onAddRecord,
  onGenerateWithAI,
  onBadRequestError,
}: IUseAgentSessionSupplierDiscoveryMutationsParams) => {
  const [
    createAgentSessionSupplierDiscoveryItem,
  ] = valueChainMapApi.endpoints.createAgentSessionSupplierDiscoveryItem.useMutation();
  const [
    updateAgentSessionSupplierDiscoveryItem,
  ] = valueChainMapApi.endpoints.updateAgentSessionSupplierDiscoveryItem.useMutation();
  const [
    deleteAgentSessionSupplierDiscoveryItem,
    { isLoading: isDeleteAgentSessionSupplierDiscoveryItemLoading },
  ] = valueChainMapApi.endpoints.deleteAgentSessionSupplierDiscoveryItem.useMutation();
  const [
    updateAgentSessionSupplierDiscoverySelection,
    { isLoading: isUpdateAgentSessionSupplierDiscoverySelectionLoading },
  ] = valueChainMapApi.endpoints.updateAgentSessionSupplierDiscoverySelection.useMutation();
  const [
    exploreAgentSessionSupplierDiscoveryAi,
  ] = valueChainMapApi.endpoints.exploreAgentSessionSupplierDiscoveryAi.useMutation();
  const { data: locationsCountries } = useGetLocationsCountriesQuery();
  const inFlightSupplierAiRequestKeyRef = useRef<string>('');

  const updateCurrentContextTableItems = useCallback((
    updater: SetStateAction<ISupplierDiscoveryTableItem[]>,
  ) => {
    if (currentUnitId === null || !tableContextKey) {
      return;
    }

    setTableItemsForContext(currentUnitId, tableContextKey, updater);
  }, [currentUnitId, setTableItemsForContext, tableContextKey]);

  const countryOptions = useMemo<IOption[]>(() => {
    if (!locationsCountries?.data) {
      return [];
    }

    return locationsCountries.data.map((country) => ({
      value: country.iso2,
      label: country.name,
    }));
  }, [locationsCountries?.data]);

  const handleCreateRecord = useCallback(async () => {
    if (!valueChainMapId || !session?.id || currentUnitId === null) {
      return;
    }

    try {
      const response = await createAgentSessionSupplierDiscoveryItem({
        valueChainMapId,
        sessionId: session.id,
        unitId: currentUnitId,
      }).unwrap();
      const nextItem = normalizeSupplierTableItem(response.data as ISupplierDiscoveryTableItem);

      updateCurrentContextTableItems((prev) => [
        nextItem,
        ...prev.filter((tableItem) => tableItem.id !== nextItem.id),
      ]);
      await syncCurrentSupplierStatusInProgress(currentUnitId, true);
      onAddRecord();
    } catch (error) {
      onBadRequestError(error);
    }
  }, [
    valueChainMapId,
    session?.id,
    currentUnitId,
    createAgentSessionSupplierDiscoveryItem,
    updateCurrentContextTableItems,
    syncCurrentSupplierStatusInProgress,
    onAddRecord,
    onBadRequestError,
  ]);

  const handleUpdateRow = useCallback(async (
    itemId: number,
    body: IUpdateAgentSessionSupplierDiscoveryItemBody,
  ) => {
    if (!valueChainMapId || !session?.id || currentUnitId === null) {
      return;
    }

    const normalizedBody: IUpdateAgentSessionSupplierDiscoveryItemBody = {
      ...('isActive' in body ? { isActive: body.isActive } : {}),
      ...('name' in body ? { name: emptyToNull(body.name) } : {}),
      ...('code' in body ? { code: emptyToNull(body.code) } : {}),
      ...('website' in body ? { website: emptyToNull(body.website) } : {}),
      ...('addressCity' in body ? { addressCity: emptyToNull(body.addressCity) } : {}),
      ...('addressState' in body ? { addressState: emptyToNull(body.addressState) } : {}),
      ...('addressCountry' in body ? { addressCountry: emptyToNull(body.addressCountry) } : {}),
      ...('industrySector' in body ? { industrySector: emptyToNull(body.industrySector) } : {}),
      ...('hqAddressCity' in body ? { hqAddressCity: emptyToNull(body.hqAddressCity) } : {}),
      ...('hqAddressState' in body ? { hqAddressState: emptyToNull(body.hqAddressState) } : {}),
      ...('hqAddressCountry' in body ? { hqAddressCountry: emptyToNull(body.hqAddressCountry) } : {}),
      ...('goodsType' in body ? { goodsType: emptyToNull(body.goodsType) } : {}),
    };
    let previousItems: ISupplierDiscoveryTableItem[] = [];

    updateCurrentContextTableItems((prev) => {
      previousItems = prev;

      return prev.map((tableItem) => (
        tableItem.id === itemId
          ? normalizeSupplierTableItem({
            ...tableItem,
            ...body,
          })
          : tableItem
      ));
    });

    try {
      const response = await updateAgentSessionSupplierDiscoveryItem({
        valueChainMapId,
        sessionId: session.id,
        unitId: currentUnitId,
        itemId,
        body: normalizedBody,
      }).unwrap();
      const responseItem = response.data as ISupplierDiscoveryTableItem;
      const nextItem = normalizeSupplierTableItem({
        ...responseItem,
        id: responseItem.id ?? itemId,
      });

      updateCurrentContextTableItems((prev) => prev.map((tableItem) => (
        tableItem.id === itemId ? nextItem : tableItem
      )));
      await syncCurrentSupplierStatusInProgress(currentUnitId, true);
    } catch (error) {
      updateCurrentContextTableItems(previousItems);
      onBadRequestError(error);
      throw error;
    }
  }, [
    valueChainMapId,
    session?.id,
    currentUnitId,
    updateAgentSessionSupplierDiscoveryItem,
    updateCurrentContextTableItems,
    syncCurrentSupplierStatusInProgress,
    onBadRequestError,
  ]);

  const isNotFoundError = (error: unknown): boolean => {
    if (typeof error !== 'object' || error === null || !('status' in error)) {
      return false;
    }
    return Number((error as FetchBaseQueryError).status) === NOT_FOUND;
  };

  const handleDeleteRow = useCallback(async (itemId: number) => {
    if (!valueChainMapId || !session?.id || currentUnitId === null) {
      return;
    }

    try {
      await deleteAgentSessionSupplierDiscoveryItem({
        valueChainMapId,
        sessionId: session.id,
        unitId: currentUnitId,
        itemId,
      }).unwrap();
    } catch (error) {
      if (!isNotFoundError(error)) {
        onBadRequestError(error);
        throw error;
      }
    }

    updateCurrentContextTableItems((prev) => prev.filter((tableItem) => tableItem.id !== itemId));
    const hasItemsAfterDelete = tableItems.some((tableItem) => tableItem.id !== itemId);
    await syncCurrentSupplierStatusInProgress(currentUnitId, hasItemsAfterDelete);
  }, [
    valueChainMapId,
    session?.id,
    currentUnitId,
    deleteAgentSessionSupplierDiscoveryItem,
    updateCurrentContextTableItems,
    tableItems,
    syncCurrentSupplierStatusInProgress,
    onBadRequestError,
  ]);

  const handleSelectAllChange = useCallback(async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!valueChainMapId || !session?.id || currentUnitId === null) {
      return;
    }

    const nextIsActive = event.target.checked;

    try {
      await updateAgentSessionSupplierDiscoverySelection({
        valueChainMapId,
        sessionId: session.id,
        unitId: currentUnitId,
        body: { isActive: nextIsActive },
      }).unwrap();
    } catch (error) {
      onBadRequestError(error);
      return;
    }

    updateCurrentContextTableItems((prev) => prev.map((tableItem) => (
      tableItem.isActive !== nextIsActive
        ? { ...tableItem, isActive: nextIsActive }
        : tableItem
    )));
    setIsSelectAllChecked(nextIsActive);
  }, [
    valueChainMapId,
    session?.id,
    currentUnitId,
    updateAgentSessionSupplierDiscoverySelection,
    updateCurrentContextTableItems,
    setIsSelectAllChecked,
    onBadRequestError,
  ]);

  const handleGenerateWithAI = useCallback(async () => {
    if (!valueChainMapId || !session?.id || currentUnitId === null) {
      return;
    }

    const requestKey = `${session.id}:${currentUnitId}`;

    if (inFlightSupplierAiRequestKeyRef.current === requestKey) {
      return;
    }

    const nextSupplierAiSocketPayload: IVCAgentSessionUnitUpdatedPayload = {
      sessionId: session.id,
      unitId: currentUnitId,
      status: VALUE_CHAIN_AI_AGENT.AI_STATUS as EVCAgentUnitStatus,
    };

    inFlightSupplierAiRequestKeyRef.current = requestKey;
    setPendingAiUnitIds((prev) => (prev.includes(currentUnitId) ? prev : [...prev, currentUnitId]));
    setSupplierAiSocketPayload(nextSupplierAiSocketPayload);

    try {
      await exploreAgentSessionSupplierDiscoveryAi({
        valueChainMapId,
        sessionId: session.id,
        unitId: currentUnitId,
      }).unwrap();
      onGenerateWithAI();
    } catch (error) {
      setSupplierAiSocketPayload(undefined);
      setPendingAiUnitIds((prev) => prev.filter((id) => id !== currentUnitId));
      onBadRequestError(error);
    } finally {
      if (inFlightSupplierAiRequestKeyRef.current === requestKey) {
        inFlightSupplierAiRequestKeyRef.current = '';
      }
    }
  }, [
    valueChainMapId,
    session?.id,
    currentUnitId,
    exploreAgentSessionSupplierDiscoveryAi,
    setPendingAiUnitIds,
    setSupplierAiSocketPayload,
    onGenerateWithAI,
    onBadRequestError,
  ]);

  return {
    handleCreateRecord,
    handleUpdateRow,
    handleDeleteRow,
    handleSelectAllChange,
    handleGenerateWithAI,
    countryOptions,
    isDeleteAgentSessionSupplierDiscoveryItemLoading,
    isUpdateAgentSessionSupplierDiscoverySelectionLoading,
  };
};

export default useAgentSessionSupplierDiscoveryMutations;
